import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress } from "@mui/material";

import ChartComponent from "../../../components/ChartComponent";
import SwalAlertComp from "../../../components/SwalAlert";
import GenericTable from "components/organisms/GenericTable";
import {
    getEvents,
    updateEventStatus,
} from "../../../helpers/trackers/eventsConfig";

import { createEventsConfigColumns } from "./EventsConfig.helpers";
import {
    SectionBlock,
    SectionTitle,
} from "./GeneralSettings/GeneralSettings.styled";

const CHART_COLORS = ["#45C300", "#0066FF", "#FFA800", "#F66700", "#45C300"];
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const EventsConfig = (props) => {
    const language = useSelector((state) => state.languagesState.languages);
    const [events, setEvents] = useState();
    const [reports, setReports] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [pendingEventIds, setPendingEventIds] = useState({});
    const [pageSize, setPageSize] = useState(25);
    const eventsRef = useRef(events);
    const pendingEventIdsRef = useRef({});

    eventsRef.current = events;

    useEffect(() => {
        if (props?.trackerId !== undefined) {
            getEventsData(props?.trackerId);
        }
    }, []);

    const getEventsData = async (trackerId) => {
        setIsLoading(true);
        let response = await getEvents(trackerId);
        if (response?.status) {
            setEvents(response?.data?.tracker_event_configuration);
            setReports(response?.data?.reports);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message,
                icon: "error",
            });
        }
        setIsLoading(false);
    };

    const setEventStatus = (eventId, status) => {
        setEvents((prev) =>
            (prev ?? []).map((event) =>
                event.event_id === eventId ? { ...event, status } : event,
            ),
        );
    };

    const handleToggleEventStatus = useCallback(
        async (eventId) => {
            if (props?.trackerId === undefined || pendingEventIdsRef.current[eventId]) {
                return;
            }

            const current = (eventsRef.current ?? []).find(
                (event) => event.event_id === eventId,
            );
            if (!current) {
                return;
            }

            const previousStatus = current.status;
            const nextStatus = previousStatus === 1 ? 0 : 1;

            pendingEventIdsRef.current = {
                ...pendingEventIdsRef.current,
                [eventId]: true,
            };
            setPendingEventIds(pendingEventIdsRef.current);
            setEventStatus(eventId, nextStatus);

            const response = await updateEventStatus(props.trackerId, eventId);
            if (!response?.status) {
                setEventStatus(eventId, previousStatus);
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message,
                    icon: "error",
                });
            }

            const nextPending = { ...pendingEventIdsRef.current };
            delete nextPending[eventId];
            pendingEventIdsRef.current = nextPending;
            setPendingEventIds(nextPending);
        },
        [language, props?.trackerId],
    );

    const getChartData = (key) => {
        let _data = reports?.All?.event_types?.[key];
        if (_data?.dates) {
            return _data?.dates.map((item) => ({
                name: item.date,
                Total: item.all_events,
            }));
        }
        return undefined;
    };

    const rows = useMemo(() => {
        if (!events || events.length === 0) {
            return [];
        }

        return [...events]
            .map((x) => {
                const sumData =
                    reports?.All?.event_types?.[x?.event?.code]?.sum_data
                        ?.all_events;
                return {
                    id: x.id,
                    name: x.event.name,
                    status: x.status,
                    eventCount: sumData || 0,
                    code: x.event.code,
                    event_id: x.event_id,
                };
            })
            .sort((a, b) => b.eventCount - a.eventCount);
    }, [events, reports]);

    const columns = useMemo(
        () =>
            createEventsConfigColumns({
                language,
                pendingEventIds,
                onToggle: handleToggleEventStatus,
            }),
        [language, pendingEventIds, handleToggleEventStatus],
    );

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <SectionBlock>
            <SectionTitle>Configure Events</SectionTitle>
            <Box sx={{ mb: 4 }}>
                Enable or disable individual events to include them in your CAPI
                setup. The chart below shows daily event counts.
            </Box>
            <GenericTable
                columns={columns}
                rows={rows}
                getRowKey={(row) => row.id}
                emptyState={
                    language?.events_config?.no_events ?? "No events found."
                }
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                tableName="tracker_event_configuration"
                renderExpanded={(row) => {
                    const chartData = getChartData(row.code);
                    if (!chartData) {
                        return null;
                    }
                    return (
                        <Box sx={{ width: "100%", my: 1.5 }}>
                            <ChartComponent
                                colors={CHART_COLORS}
                                chartData={chartData}
                            />
                        </Box>
                    );
                }}
            />
        </SectionBlock>
    );
};

export default EventsConfig;
